/**
 * All web routes within the app are handled at the frontend by vue.js. Use this
 * file to register all routes required within the app.
 */
import { createRouter, createWebHistory } from 'vue-router';
import store from './store';
import { toCanonicalFastOrderQuery } from './utils/fast-order/query';

/**
 * Define all the routes used throughout the application.
 */
const routes = [
    {
        path: '/order-action',
        name: 'Update Order',
        component: () => import(/* WebpackChunkName: "update-order" */ './views/UpdateOrder.vue'),
        meta: { preventCrawl: true }
    },
    {
        path: '/:referralCode',
        name: 'ReferralRedirect',
        beforeEnter: (to, from, next) => {
            const { referralCode } = to.params;
            if (referralCode && /^[A-Z0-9]{8}$/.test(referralCode)) {
                sessionStorage.setItem('referralCode', referralCode);
            }
            next('/');
        }
    },
    {
        path: '/',
        name: 'Welcome',
        component: () => import(/* WebpackChunkName: "welcome" */ './views/Welcome'),
        meta: { preload: true }
    },
    {
        path: '/my-space',
        name: 'My Space',
        component: () => import(/* WebpackChunkName: "my-space" */ './views/MySpace.vue'),
        meta: { preload: true, requiresAuth: true },
        children: [
            {
                path: 'box/:id',
                name: 'SpaceBox',
                component: () => import('./views/BoxDetails.vue')
            }

        ]
    },

    
    {

        path: '/refer-friends',
        name: 'Refer Friends',
        component: () => import(/* WebpackChunkName: "refer-friends" */ './views/ReferFriends.vue'),
        meta: { preload: true, requiresAuth: true }
    },
    {
        path: '/order',
        name: 'Order Space Boxes',
        component: () => import(/* WebpackChunkName: "add-space-box" */ './views/OrderBox.vue'),
        meta: { preload: true, requiresAuth: true }
    },
    {
        path: '/notifications',
        name: 'Notifications',
        component: () => import(/* WebpackChunkName: "notifications" */ './views/Notifications'),
        meta: { preload: true, requiresAuth: true }
    },
    {
        path: '/mcp',
        name: 'McpConsent',
        component: () => import(/* WebpackChunkName: "mcp-consent" */ './views/McpConsent.vue'),
        meta: { requiresAuth: true, preventCrawl: true }
    },

    {
        path: '/box/:id/schedule-pickup',
        name: 'Schedule Pickup',
        component: () => import('./views/SchedulePickup.vue'),
        meta: { requiresAuth: true },
        beforeEnter: (to, from, next) => {
            const fromSchedulePickup = from.matched?.some(route => route.name === 'Schedule Pickup');
            if (!fromSchedulePickup) {
                store.commit('boxes/RESET_PICKUP_SCHEDULING');

                if (to.name !== 'Schedule Pickup Boxes') {
                    next({ name: 'Schedule Pickup Boxes', params: { id: to.params.id }});
                    return;
                }
            }
            next();
        },
        children: [
            {
                path: '',
                redirect: to => {
                    return { name: 'Schedule Pickup Boxes', params: { id: to.params.id }};
                }
            },
            {
                path: 'boxes',
                name: 'Schedule Pickup Boxes',
                component: () => import('./components/your-space/ChoosePickupBoxesStep.vue'),
                meta: { stepIndex: 0 }
            },
            {
                path: 'address',
                name: 'Schedule Pickup Address',
                component: () => import('./components/your-space/ChooseAddressStep.vue'),
                meta: { stepIndex: 1 }
            },
            {
                path: 'date',
                name: 'Schedule Pickup Date',
                component: () => import('./components/your-space/ChooseDateStep.vue'),
                meta: { stepIndex: 2 },
                beforeEnter: to => {
                    const boxes = store.state.boxes || {};
                    const hasAddress = Boolean(boxes.selectedDeliveryAddress?.id);
                    const validDates = Object.keys(boxes.pickupDetails?.dates || {});
                    const hasDates = validDates.length > 0;
                    const hasPickupDate = Boolean(boxes.pickupDate);

                    if (hasAddress && hasDates && hasPickupDate) {
                        return true;
                    }

                    return { name: 'Schedule Pickup Address', params: { id: to.params.id }};
                }
            }
        ]
    },
    {
        path: '/box/:id/retrieve',
        name: 'Recall Box',
        component: () => import('./views/RetrieveBoxFlow.vue'),
        meta: { requiresAuth: true },
        children: [
            {
                path: '',
                redirect: to => {
                    return { name: 'Recall Box Address', params: { id: to.params.id }};
                }
            },
            {
                path: 'address',
                name: 'Recall Box Address',
                component: () => import('./components/your-space/RetrieveBox.vue')
            }
        ]
    },
    {
        path: '/settings',
        name: 'Settings',
        component: () => import(/* WebpackChunkName: "settings" */ './views/Settings.vue'),
        meta: { preload: true, requiresAuth: true },

        children: [
            { path: 'profile', name: 'SettingsProfile', component: () => import(/* WebpackChunkName: "settings" */ './components/settings/Profile') },
            { path: 'address', name: 'SettingsAddress', component: () => import(/* WebpackChunkName: "settings" */ './components/settings/Address') },
            { path: 'payment', name: 'SettingsPayment', component: () => import(/* WebpackChunkName: "settings" */ './components/settings/Payment') }
        ]
    },

    {
        path: '/checkout',
        name: 'FastOrdering',
        component: () => import('./views/FastOrder.vue'),
        meta: { preventCrawl: true }
    },
    {
        path: '/checkout/order',
        redirect: to => ({
            path: '/checkout',
            query: toCanonicalFastOrderQuery(to.query)
        })
    },
    {
        path: '/checkout/ikea',
        redirect: to => ({
            path: '/checkout',
            query: toCanonicalFastOrderQuery(to.query)
        })
    },
    {
        path: '/checkout/ikea/order',
        redirect: to => ({
            path: '/checkout',
            query: toCanonicalFastOrderQuery(to.query)
        })
    },
    {
        path: '/fastorder-update',
        redirect: to => ({
            path: '/checkout',
            query: toCanonicalFastOrderQuery(to.query)
        })
    },
    {
        path: '/:pathMatch(.*)*',
        name: 'Not Found',
        component: () => import(/* WebpackChunkName: "not-found" */ './views/NotFound'),
        meta: { preventCrawl: true }
    }
];


const router =  createRouter({
    history: createWebHistory(),
    routes,
    linkActiveClass: '',
    linkExactActiveClass: 'active',
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        }
        if (to.hash) {
            return { el: to.hash, behavior: 'smooth' };
        }
        return { top: 0, left: 0 };
    }
});


router.beforeEach((to, from, next) => {
    const isMcpReauthentication = to.query.mcp === '1' && to.query.reauth === '1';

    if (to.name === 'Welcome' && store.getters['auth/isAuthenticated'] && !isMcpReauthentication) {
        next({ name: 'My Space', replace: true });
        return;
    }

    const isFastOrderTo = to.path === '/checkout' || to.path.startsWith('/checkout/');
    const isFastOrderFrom = from.path === '/checkout' || from.path.startsWith('/checkout/');

    if (isFastOrderTo && store.getters['auth/isAuthenticated']) {
        next({ name: 'My Space' });
        return;
    }

    if (isFastOrderTo && isFastOrderFrom) {
        const toDepth   = to.path.split('/').length;
        const fromDepth = from.path.split('/').length;
        to.meta.foTransition = toDepth >= fromDepth ? 'slide-left' : 'slide-right';
    } else if (isFastOrderTo || isFastOrderFrom) {
        to.meta.foTransition = 'fade';
    }
    next();
});

export default router;
